// Fixed Hero.js with corrected syntax and proper closing tags
import React from "react";
import { Container, Box, Typography, Button, Grid, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// Button component (ensure file exists)
import ArButton from '../components/common/Button';

// Icons
import CloudIcon from "@mui/icons-material/Cloud";
import MemoryIcon from "@mui/icons-material/Memory";
import CurrencyBitcoinIcon from "@mui/icons-material/CurrencyBitcoin";

// Image (ensure file exists & spelling correct)
import DigitalVisualization from '../assets/digtal_viz.png';

function Hero() {
  const navigate = useNavigate();
  const theme = useTheme();

  const DARK_NAVY = "#0A192F";
  const LIGHT_GRAY = "#B0BEC5";
  const PRIMARY_GREEN = theme.palette.success.main;

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 8, md: 12 },
        backgroundColor: DARK_NAVY,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        textAlign: "center",
        color: "white",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center" sx={{ mb: { xs: 6, md: 10 } }}>
          {/* LEFT COLUMN */}
          <Grid item xs={12} md={7} sx={{ textAlign: { xs: "center", md: "left" } }}>
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h3"
                  component="h1"
                  fontWeight={700}
                  sx={{ fontSize: { xs: "2.5rem", sm: "3.3rem", md: "4rem" }, lineHeight: 1.1, mb: 2 }}
                >
                  We build game‑changing MVPs
                  <Box component="span" sx={{ color: PRIMARY_GREEN }}>
                    — and our own products.
                  </Box>
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{ color: LIGHT_GRAY, maxWidth: { xs: "95%", md: "100%" }, mx: { xs: "auto", md: 0 }, fontWeight: 300, mb: 2 }}
                >
                  Arocoiris Technology is a lean startup‑studio. We partner with founders to build and scale early‑stage products — while creating our own flagship platform, InvestVerse.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="body1" sx={{ color: LIGHT_GRAY, fontWeight: 300, maxWidth: "90%", mx: { xs: "auto", md: 0 } }}>
                  Product‑first engineering teams. Rapid validation. Launch‑ready execution.
                </Typography>
              </motion.div>
            </motion.div>
          </Grid>

          {/* RIGHT COLUMN IMAGE */}
          <Grid item xs={12} md={5}>
            <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 4, md: 0 } }}>
              <Box
                component="img"
                src={DigitalVisualization}
                alt="Arocoiris Digital Visualization"
                sx={{ height: { xs: 220, md: 340 }, width: "auto", borderRadius: 4, border: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 8px 32px rgba(0,0,0,0.37)", objectFit: "contain" }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* CTA SECTION */}
        <Box sx={{ mt: { xs: 6, md: 10 } }}>
          <motion.div variants={containerVariants} initial="hidden" animate="visible">
            <motion.div variants={itemVariants}>
              <Typography variant="h5" fontWeight={600} sx={{ color: LIGHT_GRAY, mb: 1 }}>
                Build With a Team That Thinks Like Founders.
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Typography variant="h4" fontWeight={700} sx={{ color: "white", fontSize: { xs: "1.75rem", md: "2.4rem" }, mb: 2 }}>
                Early‑Stage Product Development. Real Business Outcomes.
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Typography variant="body1" sx={{ maxWidth: "700px", mx: "auto", color: LIGHT_GRAY, mb: 4 }}>
                We cut months off your launch timeline. Whether you're a founder building your MVP — or exploring InvestVerse — we help you move fast and intelligently.
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Button
                variant="contained"
                color="success"
                size="large"
                sx={{ px: 6, py: 1.5, fontSize: "1rem", fontWeight: 700, borderRadius: 2 }}
                onClick={() => navigate("/contact")}
              >
                Let’s Build Your Product
              </Button>
            </motion.div>
            <motion.div variants={itemVariants} style={{ marginTop: theme.spacing(3) }}>
              <ArButton sx={{ minWidth: 160, fontWeight: 600 }} onClick={() => navigate("/InvestVerse")}>Explore InvestVerse</ArButton>
              <ArButton variant="outline" sx={{ minWidth: 160, ml: 2, fontWeight: 600 }} onClick={() => navigate("/contact")}>Book Discovery</ArButton>
            </motion.div>
          </motion.div>
        </Box>

        {/* TECHNOLOGY FOCUS SECTION */}
        <Box sx={{ mt: { xs: 8, md: 15 }, pb: 6 }}>
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.5, delay: 0.5 }}>
            <Typography variant="h5" sx={{ color: LIGHT_GRAY, mb: 4, fontWeight: 300 }}>
              Our Technology Focus
            </Typography>

            <Grid container spacing={{ xs: 2, md: 8 }} justifyContent="center">
              {[
                "Artificial Intelligence",
                "Cloud Solutions",
                "Blockchain"
              ].map((label, index) => (
                <Grid item xs={4} sm={3} md={2} key={index}>
                  <motion.div whileHover={{ scale: 1.1 }} style={{ cursor: "pointer", transition: "0.3s" }}>
                    <Box sx={{ color: PRIMARY_GREEN }}>
                      {label === "Artificial Intelligence" && <MemoryIcon sx={{ fontSize: { xs: 40, md: 55 } }} />}
                      {label === "Cloud Solutions" && <CloudIcon sx={{ fontSize: { xs: 40, md: 55 } }} />}
                      {label === "Blockchain" && <CurrencyBitcoinIcon sx={{ fontSize: { xs: 40, md: 55 } }} />}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: "0.7rem", md: "0.9rem" } }}>
                      {label}
                    </Typography>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
}

export default Hero;
